import { Filter, FilterType } from '@shared/protos/filter';
import { PRODUCT_GROUP_OPTIONS, ProductGroupEnum } from '@shared/protos/user';
import { ColDef } from 'ag-grid-enterprise';

// What an organization may act as. The type is the designation Flux controls; the links
// carry the scope. PRINCIPAL is a label and grants nothing.
export enum OrganizationTypeEnum {
  PRINCIPAL = 'principal',
  AGENT = 'agent',
  CLIENT = 'client',
}

export const ORGANIZATION_TYPE_OPTIONS: OrganizationTypeEnum[] = [
  OrganizationTypeEnum.PRINCIPAL,
  OrganizationTypeEnum.AGENT,
  OrganizationTypeEnum.CLIENT,
];

export type Organization = {
  id: number;
  name: string;
  zoho_organization_id: string;
  cme_firm_id: string;
  cme_firm_name_id: string;
  cme_firm_name: string;
  trading_accounts: string[];
  types: OrganizationTypeEnum[];
};

export type OrganizationUpdate = {
  types: OrganizationTypeEnum[];
};

export type OrganizationLinkBase = {
  agent_organization_id: number;
  client_organization_id: number;
  scope_product_group: ProductGroupEnum;
  scope_products: string[];
  enabled: boolean;
};

export type OrganizationLink = OrganizationLinkBase & {
  id: number;
  updated_by_user_id: string | null;
  agent_organization_name: string | null;
  client_organization_name: string | null;
};

// Three numbers with one owner each, and the ordering between them is the whole control:
// 0 <= position_lots <= allocated_lots <= ceiling_lots. Flux moves the ceiling, the agent
// moves the allocation, only fills move the position. Integer lots throughout.
//
// headroom_lots is allocated less position and comes down on the wire, so nothing here
// subtracts two columns to work out whether an organization can still buy.
export type OrganizationTradeLimit = {
  id: number;
  organization_id: number;
  product_symbol: string;
  ceiling_lots: number;
  allocated_lots: number;
  position_lots: number;
  headroom_lots: number;
  version: number;
  updated_by_user_id: string | null;
  organization_name: string | null;
};

export type OrganizationTradeLimitCreate = {
  ceiling_lots: number;
};

export const ORGANIZATION_COLUMNS: ColDef<Organization>[] = [
  {
    field: 'id',
    headerName: 'ID',
    flex: 1,
  },
  {
    field: 'name',
    headerName: 'Name',
    flex: 3,
  },
  {
    field: 'types',
    headerName: 'Types',
    flex: 2,
    valueFormatter: ({ value }) => (value ?? []).join(', '),
  },
  {
    field: 'zoho_organization_id',
    headerName: 'Zoho ID',
    flex: 2,
  },
  {
    field: 'cme_firm_name',
    headerName: 'CME Firm',
    flex: 2,
  },
  {
    field: 'trading_accounts',
    headerName: 'Trading Accounts',
    flex: 3,
    valueFormatter: ({ value }) => (value ?? []).join(', '),
  },
];

// The API narrows the list to the organizations in scope for one group
export const ORGANIZATION_FILTERS: Filter[] = [
  { type: FilterType.SELECT, name: 'scope_product_group', label: 'Product Group', options: PRODUCT_GROUP_OPTIONS },
];

// Which side of a link the organization being viewed sits on. The other side is the
// counterparty, and it is the only one worth a column: repeating the organization whose
// page this is in every row reads as "agent over agent".
export type LinkSide = 'agent' | 'client';

export const organizationLinkColumns = (side: LinkSide): ColDef<OrganizationLink>[] => [
  {
    field: 'id',
    headerName: 'ID',
    flex: 1,
  },
  side === 'agent'
    ? {
        field: 'client_organization_name',
        headerName: 'Client',
        flex: 3,
        valueFormatter: ({ value, data }) => value ?? `#${data?.client_organization_id}`,
      }
    : {
        field: 'agent_organization_name',
        headerName: 'Agent',
        flex: 3,
        valueFormatter: ({ value, data }) => value ?? `#${data?.agent_organization_id}`,
      },
  {
    field: 'scope_product_group',
    headerName: 'Product Group',
    flex: 2,
  },
  {
    field: 'scope_products',
    headerName: 'Scope Products',
    flex: 2,
    valueFormatter: ({ value }) => (value ?? []).join(', '),
  },
  {
    field: 'enabled',
    headerName: 'Enabled',
    flex: 1,
  },
  {
    field: 'updated_by_user_id',
    headerName: 'Updated By',
    flex: 2,
  },
];

export const ORGANIZATION_LIMIT_COLUMNS: ColDef<OrganizationTradeLimit>[] = [
  {
    field: 'product_symbol',
    headerName: 'Product',
    flex: 2,
    valueFormatter: ({ value }) => (value ?? '').toLocaleUpperCase(),
  },
  {
    field: 'ceiling_lots',
    headerName: 'Ceiling',
    headerTooltip: 'Maximum lots the organization may hold. Flux sets this, and only Flux moves it',
    flex: 1,
  },
  {
    field: 'allocated_lots',
    headerName: 'Allocated',
    headerTooltip:
      'Lots of the ceiling the agent has granted this organization. The agent moves it, never above the ceiling and never below the position already held',
    flex: 1,
  },
  {
    field: 'position_lots',
    headerName: 'Position',
    headerTooltip: 'Lots the organization holds. Only fills move it',
    flex: 1,
  },
  {
    field: 'headroom_lots',
    headerName: 'Headroom',
    headerTooltip: 'Lots still available to buy, the allocation less the position. 0 means the organization cannot buy',
    flex: 1,
  },
  {
    field: 'updated_by_user_id',
    headerName: 'Updated By',
    flex: 2,
  },
];
